import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { GraduationCap, ScanSearch, Handshake, Plane, Wallet, Trophy, Users, Building2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useCms } from "@/hooks/useCms";

const ICON_MAP: Record<string, React.ElementType> = {
  GraduationCap, ScanSearch, Handshake, Plane, Wallet, Trophy, Users, Building2,
};

const DEFAULT_SERVICES = [
  { icon: "GraduationCap", title: "1. Football Academy", desc: "Structured training programs designed to develop technical skills, discipline, leadership, and physical fitness.", points: ["Professional football training", "Athletic development", "Tactical & technical coaching", "Youth mentorship", "Academic and sports balance"], href: "/services/football-academy" },
  { icon: "ScanSearch", title: "2. Talent Scouting", desc: "International scouting network across Africa and Europe evaluating promising players for professional environments.", points: ["Local leagues & tournaments", "Football academies", "School competitions", "Community sports programs"], href: "/services/talent-scouting" },
  { icon: "Handshake", title: "3. Player Representation", desc: "Professional management services ensuring athletes can focus on performance while careers are guided strategically.", points: ["Career planning & development", "Contract negotiations", "Player representation", "Legal advisory", "Sponsorship opportunities"], href: "/services/player-representation" },
  { icon: "Plane", title: "4. International Transfers", desc: "Facilitating transfers between clubs, academies, and professional teams across continents.", points: ["International scouting exposure", "Trials with pro clubs", "Transfer negotiations", "Visa & relocation assistance", "Career transition support"], href: "/services/international-transfers" },
  { icon: "Wallet", title: "5. Career Investment", desc: "Removing financial barriers allowing talented athletes to pursue their dreams without limitation.", points: ["Talent development sponsorship", "Career investment initiatives", "Training support programs", "Sports scholarships"], href: "/services/career-investment" },
  { icon: "Trophy", title: "6. Sports Events & Leagues", desc: "Organizing and sponsoring sports activities to create platforms for young players to showcase skills.", points: ["Youth football leagues", "Talent showcase tournaments", "Community sports competitions", "Development programs"], href: "" },
];

const DEFAULT_STEPS = [
  { num: "01", name: "Discovery", desc: "We identify and evaluate your talent through scouting events and referrals" },
  { num: "02", name: "Development", desc: "Enroll in our structured academy program and mentorship" },
  { num: "03", name: "Representation", desc: "We negotiate contracts and manage your professional career" },
  { num: "04", name: "Opportunity", desc: "Get placed with professional clubs and leagues worldwide" },
];

export default function Services() {
  const { getText, getMeta } = useCms();

  const heroSubtitle = getText("services_hero", "Comprehensive, end-to-end management and development solutions for the modern athlete.");

  const services = getMeta<{ items: typeof DEFAULT_SERVICES }>("services_list", { items: DEFAULT_SERVICES }).items ?? DEFAULT_SERVICES;

  const youthText = getText("services_youth", "Beyond professional sports management, we believe in the power of sports as a tool for youth empowerment, education, and social development.");
  const youthMeta = getMeta<{ bullets?: string[] }>("services_youth", {
    bullets: ["Promote discipline and teamwork", "Encourage positive lifestyles", "Provide mentorship and leadership training", "Create opportunities for underprivileged talents"],
  });

  const networkText = getText("services_network", "To strengthen our impact and ensure legitimacy, we collaborate strategically across the international sports ecosystem.");
  const networkMeta = getMeta<{ bullets?: string[] }>("services_network", {
    bullets: ["Partnerships with professional clubs & academies", "Collaborations with government agencies", "Visa & document legalization assistance", "Navigating bureaucratic bottlenecks for athletes"],
  });

  const steps = getMeta<{ items: typeof DEFAULT_STEPS }>("services_how_it_works", { items: DEFAULT_STEPS }).items ?? DEFAULT_STEPS;

  const ctaMeta = getMeta<{ heading?: string; subtext?: string; buttonText?: string }>("services_cta", {
    heading: "START YOUR JOURNEY TODAY",
    subtext: "Let our experts evaluate your potential and craft your career path.",
    buttonText: "Contact Us Now",
  });

  return (
    <>
      <SeoHead
        title="Our Services"
        description="Discover Eliada Sports Agency's core services: talent scouting, football academy, international player transfers, player representation, and career investment for elite athletes."
        path="/services"
      />
      <PageTransition className="pt-24 pb-0">
      {/* ── Hero Banner ── */}
      <div className="relative py-28 bg-secondary overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=2000&auto=format&fit=crop" alt="" aria-hidden="true" className="w-full h-full object-cover object-center opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/30 to-background/80" />
          <div className="absolute inset-0" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,191,0,0.015) 40px, rgba(255,191,0,0.015) 41px)" }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 bg-primary/10 text-primary mb-6">
            <Trophy size={13} />
            <span className="text-xs font-display tracking-widest uppercase font-semibold">What We Offer</span>
          </motion.div>
          <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-5">CORE AREAS OF <span className="text-primary">OPERATION</span></h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">{heroSubtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mt-20 mb-0" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-32">
          {services.map((srv, i) => {
            const Icon = ICON_MAP[srv.icon] ?? Trophy;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Card className="h-full flex flex-col hover:-translate-y-2 transition-transform duration-300 border-white/5 hover:border-primary/50 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Icon size={120} className="text-primary -mr-8 -mt-8" />
                  </div>
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary"><Icon size={24} /></div>
                    <CardTitle className="text-xl leading-tight">{srv.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow flex flex-col">
                    <p className="text-sm mb-6 flex-grow">{srv.desc}</p>
                    <ul className="space-y-2 mb-6">
                      {srv.points.map((point, idx) => (
                        <li key={idx} className="text-xs text-gray-400 flex items-start gap-2">
                          <div className="mt-1 w-1 h-1 rounded-full bg-primary shrink-0" />{point}
                        </li>
                      ))}
                    </ul>
                    {srv.href && (
                      <Link href={srv.href}><span className="text-primary text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all mt-auto">Learn More <ArrowRight size={14} /></span></Link>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-32">
          <div className="bg-secondary rounded-2xl p-10 border border-white/5">
            <Users className="w-10 h-10 text-primary mb-6" />
            <h3 className="text-2xl font-display font-bold text-white mb-4">Youth Empowerment</h3>
            <p className="text-muted-foreground mb-4">{youthText}</p>
            <ul className="text-sm text-gray-400 space-y-2">
              {(youthMeta.bullets ?? []).map((b, i) => <li key={i}>• {b}</li>)}
            </ul>
          </div>
          <div className="bg-secondary rounded-2xl p-10 border border-white/5">
            <Building2 className="w-10 h-10 text-primary mb-6" />
            <h3 className="text-2xl font-display font-bold text-white mb-4">Global Network & Partnerships</h3>
            <p className="text-muted-foreground mb-4">{networkText}</p>
            <ul className="text-sm text-gray-400 space-y-2">
              {(networkMeta.bullets ?? []).map((b, i) => <li key={i}>• {b}</li>)}
            </ul>
          </div>
        </div>

        <div className="mb-32 relative">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">HOW IT <span className="text-primary">WORKS</span></h2>
          </div>
          <div className="hidden lg:block absolute top-1/2 left-0 w-full border-t-2 border-dashed border-white/20 -translate-y-12 z-0" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="bg-background border border-white/5 p-8 rounded-2xl text-center relative group hover:border-primary/50 transition-colors">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-display font-bold text-2xl mx-auto mb-6 shadow-[0_0_20px_rgba(255,191,0,0.3)]">{step.num}</div>
                <h3 className="font-display font-bold text-white text-xl mb-3">{step.name}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <section className="w-full bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex flex-col md:flex-row items-center justify-between text-center md:text-left gap-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-2 drop-shadow-sm">{ctaMeta.heading}</h2>
            <p className="text-lg text-gray-800 font-medium">{ctaMeta.subtext}</p>
          </div>
          <Link href="/contact"><Button size="lg" className="bg-gray-900 text-white hover:bg-black w-full sm:w-auto px-8 py-6 text-lg">{ctaMeta.buttonText ?? "Contact Us Now"}</Button></Link>
        </div>
      </section>
      </PageTransition>
    </>
  );
}
